import React, { useEffect, useState } from 'react';
import { Modal, ModalHeader, ModalBody, Button } from 'reactstrap';
import api from '../../../api';
import DataTable from './DataTable';

// Go-live parity (2026-09-24): the legacy board app (DispBoardQuality) opened a
// popup when a queue card or an employee row was clicked — incoming
// inspection, QFinal queue, open RMAs, open DMRs, or one employee's labour
// history — with an Excel export. This modal shows the same rows from
// GET /api/display-board/{board}/detail/{kind} or /employee-history, with a
// CSV export in place of the Excel file.

const WAIT = { key: 'WaitDays', label: 'Wait Days' };

export const DETAIL_COLUMNS = {
    incoming: [
        { key: 'PartNum', label: 'Part #' },
        { key: 'PONum', label: 'PO #' },
        { key: 'RunQty', label: 'Run Qty' },
        { key: 'ReceiptDate', label: 'Rcpt Date' },
        WAIT,
    ],
    qfinal: [
        { key: 'JobNum', label: 'Job #' },
        { key: 'PartNum', label: 'Part #' },
        { key: 'RunQty', label: 'Run Qty' },
        { key: 'StartDate', label: 'Start Date' },
        { key: 'JobDueDate', label: 'Job Due Date' },
        { key: 'OpDueDate', label: 'Op. Due Date' },
        WAIT,
    ],
    rma: [
        { key: 'PartNum', label: 'Part #' },
        { key: 'Customer', label: 'Customer' },
        { key: 'RMANum', label: 'RMA #' },
        { key: 'StartDate', label: 'Start Date' },
        WAIT,
        { key: 'RcvDate', label: 'Rcv Date' },
    ],
    dmr: [
        { key: 'PartNum', label: 'Part #' },
        { key: 'JobNum', label: 'Job #' },
        { key: 'Qty', label: 'Qty' },
        { key: 'DMRNum', label: 'DMR #' },
        { key: 'StartDate', label: 'Start Date' },
        WAIT,
        { key: 'Comment', label: 'Comment' },
    ],
    history: [
        { key: 'Operation', label: 'Operation' },
        { key: 'JobNum', label: 'Job #' },
        { key: 'PartNum', label: 'Part #' },
        { key: 'QtyCompleted', label: 'Qty Completed' },
        { key: 'JobQty', label: 'Job Qty' },
        { key: 'Date', label: 'Date' },
        { key: 'StartTime', label: 'Start Time' },
        { key: 'EndTime', label: 'End Time' },
    ],
};

/** Request for a queue drill-down: board = quality | shukla. */
export const queueDetail = (board, kind, title) => ({
    title,
    url: `/display-board/${board}/detail/${kind}`,
    params: undefined,
    columns: DETAIL_COLUMNS[kind],
    fileName: `${board}-${kind}`,
});

/** Request for one employee's last-30-days labour history (legacy ViewPopups). */
export const historyDetail = (row) => {
    const name = String(row?.EmployeeName ?? row?.employeeName ?? '').trim();
    if (!name) return null;
    return {
        title: `${name} — 30 Days History`,
        url: '/display-board/employee-history',
        params: { name },
        columns: DETAIL_COLUMNS.history,
        fileName: `history-${name.replace(/[^A-Za-z0-9]+/g, '-')}`,
    };
};

const csvCell = (v) => {
    const s = v === null || v === undefined ? '' : String(v);
    return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const downloadCsv = (columns, rows, fileName) => {
    const lines = [columns.map((c) => csvCell(c.label)).join(',')];
    rows.forEach((r) => lines.push(columns.map((c) => csvCell(r[c.key])).join(',')));
    const blob = new Blob([lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${fileName || 'display-board'}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

const DetailModal = ({ request, onClose }) => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!request) return undefined;
        let alive = true;
        setRows([]);
        setError(null);
        setLoading(true);
        api.get(request.url, { params: request.params })
            .then((res) => { if (alive) setRows(res.data?.rows || []); })
            .catch((err) => {
                if (alive) setError(err.response?.data?.error || err.message || 'Failed to load the details');
            })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [request]);

    return (
        <Modal isOpen={!!request} toggle={onClose} size="xl" centered scrollable>
            <ModalHeader toggle={onClose}>{request?.title}</ModalHeader>
            <ModalBody className="p-0">
                <div className="d-flex justify-content-between align-items-center px-3 py-2 border-bottom">
                    <small className="text-muted">{loading ? 'Loading…' : `${rows.length} row(s)`}</small>
                    <Button
                        size="sm"
                        color="success"
                        disabled={loading || rows.length === 0}
                        onClick={() => downloadCsv(request.columns, rows, request.fileName)}
                    >
                        <i className="ri-file-excel-2-line me-1"></i>Export CSV
                    </Button>
                </div>
                {error && (
                    <div className="alert alert-danger m-3" role="alert">
                        <i className="ri-error-warning-line me-2"></i>{error}
                    </div>
                )}
                {request && (
                    <DataTable
                        columns={request.columns}
                        rows={rows}
                        loading={loading}
                        emptyMessage="Nothing waiting"
                    />
                )}
            </ModalBody>
        </Modal>
    );
};

export default DetailModal;
